// src/pages/sow-analysis/useSowAnalysisPage.js
//
// All state, derived values, and handlers for the combined SOW Analysis
// page -- merges what used to be useSowHistoryAnalysis.js (the "Analyze
// SOWs" upload/create flow) and useSowAnalysisViewer.js (source tabs,
// Project/Opportunity filter, search, detail modal) into one hook, since
// they're both just different facets of the same SowAnalysisAggregate
// history.
//
// One canonical history fetch: fetchSowHistory({source, opportunityId,
// engagementId, userEmail}) -- a strict superset of the old
// fetchHistoricalSowHistory(limit, email), which supported neither the
// source tabs nor either filter. This is what makes "All / SFDC / UI
// Upload" work for records from both origins (manual upload today;
// SFDC-triggered once opportunity_controller.py's upload_sow_document is
// actually implemented -- currently a stub).
//
// Account Name and Project (for a NEW analysis) come from the workspace's
// own Account/Project switcher (useWorkspace()) -- Opportunity ID/Contract
// Type/Stage/Drafter/Reviewer/SL-GD Email prefill from the selected
// Project's linked Opportunity when it has one (EngagementAggregate.
// opportunity_id -- see registerProject in useSowDraft.js for where that
// link gets set); a Project with no linked Opportunity only prefills
// Contract Type (stored on the Project itself), leaving the rest blank and
// manually editable -- this is what covers a SOW with no CRM Opportunity
// behind it.
//
// downloadExcel calls GET /sow-tests/report-excel/{id}; downloadSowFile
// calls GET /sow-tests/files/{id} (only analyses with a stored original).
// downloadJson is a client-side re-serialization of raw_json.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  analyzeSowBatchUpload,
  importSowAnalysisPlanningSow,
  downloadBlob,
  downloadExcelReport,
  downloadSowFile as fetchSowFileBlob,
  fetchSowHistory,
  fetchSowHistoryDetail,
} from "../../api/sowApi";
import { getApiError, sowDraftApi } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { useWorkspace } from "../../context/WorkspaceContext";
import { getViewerEmail as getUserEmail } from "../../utils/session";
import { detectContractType } from "../sow-draft/sowDraftHelpers";
import { safeParseRaw } from "./sowAnalysisHelpers";

// Must match ContractTypeEnum (domain/sow/value_objects/contract_type.py)
// exactly -- the backend rejects anything else, including free text like
// an Opportunity's raw "Time and Material" (see detectContractType below,
// which normalizes into this vocabulary).
export const CONTRACT_TYPES = ["T&M", "Fixed Bid", "Managed Services", "Custom"];

// "ui" maps to HISTORICAL_UPLOAD -- that's the tag UploadBatchAnalyzeHandler
// actually writes for every SOW submitted through this page's own "Analyze
// SOWs" modal (see SowAnalysisSource). UI_TEST is a separate, distinct tag
// used only by the orphaned single-file /analyze route (no frontend page
// calls it), so mapping "ui" to it here would show nothing for every real
// manual upload -- which is exactly what was happening before this fix.
const SOURCE_WIRE_VALUES = { sfdc: "SFDC", ui: "HISTORICAL_UPLOAD" };

const emptyUploadForm = {
  sfOpportunityId: "",
  contractType: "T&M",
  stage: "",
  sowDrafter: "",
  sowReviewer: "",
  slGdEmail: "",
};

function clampPct(n) {
  if (typeof n !== "number" || Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

export function useSowAnalysisPage() {
  const userEmail = useMemo(() => getUserEmail(), []);
  const { currentAccount, currentEngagement, engagements, createEngagement } = useWorkspace();
  const { hasGlobalPermission, hasPermission } = useAuth();

  // --- upload form ("Analyze SOWs") ---
  const [uploadForm, setUploadForm] = useState(emptyUploadForm);
  const [files, setFiles] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  // Defaults to the globally-selected Project, but overridable for this one
  // analysis without leaving the page (see the Select in UploadSowModal).
  const [selectedEngagementId, setSelectedEngagementId] = useState(null);
  const [newProjectModalOpen, setNewProjectModalOpen] = useState(false);
  // The linked Opportunity's display name, if resolved -- not an editable
  // form field (there's no "Opportunity Name" input), just carried along
  // so the analysis record itself gets a real opportunity_name instead of
  // always being blank.
  const [resolvedOpportunityName, setResolvedOpportunityName] = useState("");

  // --- browse: source tabs + Project/Opportunity filter ---
  const [activeTab, setActiveTab] = useState("all"); // "all" | "sfdc" | "ui"
  const [opportunities, setOpportunities] = useState([]);
  // Encoded as "project:<engagement_id>" or "opportunity:<id>" so a single
  // dropdown can drive either filter without the two id spaces colliding
  // -- see AnalysisHistoryTable.jsx's own grouping.
  const [selectedFilterValue, setSelectedFilterValue] = useState("");
  const selectedProjectId = selectedFilterValue.startsWith("project:")
    ? selectedFilterValue.slice("project:".length)
    : "";
  const selectedOpportunityId = selectedFilterValue.startsWith("opportunity:")
    ? selectedFilterValue.slice("opportunity:".length)
    : "";

  // --- history list ---
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // --- detail ---
  const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // --- ui feedback ---
  const [activeJobs, setActiveJobs] = useState([]);
  const [viewingJobId, setViewingJobId] = useState(null);
  
  const [busy, setBusy] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [downloadingSowFile, setDownloadingSowFile] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const loadOpportunities = useCallback(async () => {
    try {
      setError("");
      const resp = await sowDraftApi.listOpps(1, 100);
      // Real opportunity fields (name, sfdc_account_id, salesforce_opportunity_id,
      // deal_value) differ from what this page's components read -- normalize
      // once here rather than touching every read site. sfdc_account_id is a
      // bare id with no display name available from this endpoint.
      const list = (resp?.items || []).map((o) => ({
        ...o,
        opportunity_name: o.name,
        account_name: o.sfdc_account_id ? `Account #${o.sfdc_account_id}` : "—",
        sf_opportunity_id: o.salesforce_opportunity_id,
        opportunity_amount: o.deal_value,
      }));
      setOpportunities(list);
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 401) {
        setError("Authentication required. Please log in.");
      } else {
        setError("Failed to load opportunities.");
      }
    }
  }, []);

  const loadAnalysisHistory = useCallback(
    async (source, opportunityId, opps, engagementId, accountId) => {
      try {
        setError("");
        setLoading(true);
        // The backend's history opportunity_id filter is keyed by the SFDC
        // id string (SowAnalysisAggregate.opportunity_id), a different id
        // space from the picker's internal numeric id -- resolve it from
        // the already-loaded opportunity list rather than sending the
        // internal id straight through (that would silently match nothing).
        const selectedOpp = opportunityId
          ? (opps || []).find((o) => String(o.id) === String(opportunityId))
          : null;

        const resp = await fetchSowHistory({
          source: source && source !== "all" ? SOURCE_WIRE_VALUES[source] : undefined,
          opportunityId: selectedOpp?.sf_opportunity_id || undefined,
          engagementId: engagementId || undefined,
          accountId: accountId || undefined,
          userEmail,
        });
        setAnalysisHistory(resp?.items || []);
      } catch (err) {
        console.error(err);
        if (err?.response?.status === 401) {
          setError("Authentication required for SFDC records. Please ensure you're logged in.");
        } else {
          setError("Failed to load analysis history.");
        }
      } finally {
        setLoading(false);
      }
    },
    [userEmail]
  );

  useEffect(() => {
    loadOpportunities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetches whenever the workspace's Account switcher changes, not just
  // the in-page tab/filter -- this page's history is narrowed to whichever
  // Account is currently selected (see GetAnalysisHistoryQuery's
  // filter_account_id), so switching Accounts must reload it the same way
  // switching the Project/Opportunity filter already does.
  useEffect(() => {
    loadAnalysisHistory(
      activeTab, selectedOpportunityId, opportunities, selectedProjectId,
      currentAccount?.account_id
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedOpportunityId, opportunities, selectedProjectId, currentAccount?.account_id]);

  // Keeps the browse filter following the workspace's own Project switcher
  // -- switching Project in the top bar changes which Project's SOW history
  // is shown here, the same way switching Account already does above via
  // currentAccount. An in-page override (picking a different Project or
  // Opportunity from the filter dropdown) sticks until the global selection
  // itself changes again -- the top bar stays the source of truth, same
  // pattern as selectedEngagementId's own sync effect below. Updating
  // selectedFilterValue is enough to trigger a refetch: selectedProjectId
  // (derived from it) is already in the effect above's dependency array.
  useEffect(() => {
    setSelectedFilterValue(
      currentEngagement?.engagement_id ? `project:${currentEngagement.engagement_id}` : ""
    );
  }, [currentEngagement?.engagement_id]);

  useEffect(() => {
    if (!message) return;
    const id = setTimeout(() => setMessage(""), 4000);
    return () => clearTimeout(id);
  }, [message]);

  const filteredHistory = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return analysisHistory;
    return analysisHistory.filter((h) => {
      const hay = [h.id, h.sf_opportunity_id, h.opportunity_name, h.account_name, h.contract_type, h.source, h.stage]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [analysisHistory, search]);

  const onUploadFormChange = (e) => {
    const { name, value } = e.target;
    setUploadForm((f) => ({ ...f, [name]: value }));
  };

  const selectedEngagement = useMemo(
    () => engagements.find((e) => e.engagement_id === selectedEngagementId) || null,
    [engagements, selectedEngagementId]
  );

  // Whether the fields below actually came from a linked Opportunity, as
  // opposed to being left blank for manual entry -- used only to show a
  // "from the linked Opportunity" hint in UploadSowModal. Every field stays
  // editable either way (this form is manual-entry-first): a specific SOW
  // file can legitimately need a different Stage, drafter, or contract
  // type than whatever the Opportunity currently has on file.
  const opportunityLinked = Boolean(selectedEngagement?.opportunity_id);

  // Mirrors ProjectsPage.jsx's / useSowDraft.js's own gating for the same
  // action.
  const canCreateProject = Boolean(
    currentAccount &&
    (hasGlobalPermission("conductor:engagement:create") ||
      hasPermission("conductor:engagement:create", { accountId: currentAccount.account_id }))
  );

  // Same backend call as ProjectsPage.jsx's handleRegister. No Opportunity
  // is already in view on this page, so RegisterProjectModal shows its own
  // optional Opportunity picker (payload.opportunityId) rather than a
  // preset one -- same as Project Management's own "+ New Project" flow.
  const registerProject = useCallback(
    async (payload) => {
      const created = await createEngagement({
        client_name: payload.name,
        platform: payload.platform,
        contract_type: payload.contractType,
        opportunity_id: payload.opportunityId,
      });
      setSelectedEngagementId(created.engagement_id);
      return { ...created, name: created.client_name };
    },
    [createEngagement]
  );

  // Keeps the modal's Project selection following the workspace's own
  // switcher -- switching Account or Project in the top bar updates
  // currentEngagement, which this mirrors into selectedEngagementId, live,
  // even while the modal is already open. An in-modal override (via the
  // Select in UploadSowModal) sticks until the global selection itself
  // changes again -- the top bar stays the source of truth, same as every
  // other page.
  useEffect(() => {
    setSelectedEngagementId(currentEngagement?.engagement_id || null);
  }, [currentEngagement?.engagement_id]);

  const openUploadModal = () => {
    setViewingJobId(null);
    setUploadForm(emptyUploadForm);
    setFiles([]);
    setProgress(0);
    setStatusText("");
    setError("");
    setResolvedOpportunityName("");
    setBusy(false);
    setUploadModalOpen(true);
  };

  const openUploadModalForJob = (jobId) => {
    const job = activeJobs.find(j => j.id === jobId);
    if (!job) return;
    setViewingJobId(jobId);
    setUploadForm(job.uploadForm);
    setFiles(job.files);
    setProgress(job.progress);
    setStatusText(job.statusText);
    setError(job.error);
    setBusy(job.busy);
    setSelectedEngagementId(job.selectedEngagementId);
    setResolvedOpportunityName(job.resolvedOpportunityName);
    setUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    setUploadModalOpen(false);
    setViewingJobId(null);
    setError("");
  };

  const dismissJob = (e, jobId) => {
    e.stopPropagation();
    setActiveJobs(prev => prev.filter(j => j.id !== jobId));
  };


  // Prefills from whichever Project is selected: Contract Type always comes
  // from the Project itself (stored there regardless of any Opportunity
  // link); Opportunity ID/Stage/Drafter/Reviewer/SL-GD Email only prefill
  // when that Project actually has a linked Opportunity. Fields stay
  // editable either way -- contract type feeds the scoring pipeline, so
  // locking it read-only could be wrong for a SOW predating the Project's
  // current contract type.
  useEffect(() => {
    if (!uploadModalOpen || !selectedEngagement) return;

    if (!selectedEngagement.opportunity_id) {
      // No linked Opportunity on this Project -- clear whatever the
      // previously-selected Project's Opportunity may have populated,
      // rather than leaving it stale when switching between Projects.
      setUploadForm((f) => ({
        ...emptyUploadForm,
        contractType: selectedEngagement.contract_type
          ? detectContractType(selectedEngagement.contract_type)
          : f.contractType,
      }));
      setResolvedOpportunityName("");
      return;
    }

    setUploadForm((f) => ({
      ...f,
      contractType: selectedEngagement.contract_type
        ? detectContractType(selectedEngagement.contract_type)
        : f.contractType,
    }));

    let cancelled = false;
    (async () => {
      try {
        const opp = await sowDraftApi.getOpp(selectedEngagement.opportunity_id);
        if (cancelled) return;
        setResolvedOpportunityName(opp.name || "");
        setUploadForm((f) => ({
          ...f,
          sfOpportunityId: opp.salesforce_opportunity_id || f.sfOpportunityId,
          // Opportunity.contract_type is free text (e.g. "Time and
          // Material") -- normalize into SOW's own ContractType vocabulary
          // (domain/sow/value_objects/contract_type.py) before it ever
          // reaches the form, or the analyze-batch call rejects it.
          contractType: opp.contract_type ? detectContractType(opp.contract_type) : f.contractType,
          stage: opp.stage || f.stage,
          sowDrafter: opp.sow_drafter_email || f.sowDrafter,
          sowReviewer: opp.sow_reviewer_email || f.sowReviewer,
          slGdEmail: opp.sl_gd_email || f.slGdEmail,
        }));
      } catch (err) {
        console.error(err);
        // Non-fatal -- fields just stay blank/editable, same as a Project
        // with no linked Opportunity.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [uploadModalOpen, selectedEngagement]);

  const selectAnalysis = async (id, baseRow, opts = {}) => {
    setSelectedAnalysisId(id);
    setDetailLoading(true);
    setSelectedDetail(baseRow || null);
    setDetailModalOpen(true);

    try {
      const detail = await fetchSowHistoryDetail(id, userEmail);
      setSelectedDetail(detail);
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 403) {
        setError("You don't have access to this SFDC record.");
      } else if (err?.response?.status === 401) {
        setError("Authentication required for SFDC records.");
      } else if (!opts?.silent) {
        setError("Failed to load analysis detail.");
      }
      setSelectedDetail(baseRow || null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailModal = () => setDetailModalOpen(false);

  const handleAnalyze = async (e) => {
    e.preventDefault();
    setError("");

    if (!selectedEngagementId) return setError("Please select a Project.");
    if (!files.length) return setError("Please select at least one SOW file.");

    const jobId = viewingJobId || Date.now().toString();
    const isRetry = viewingJobId != null;

    setBusy(true);
    setProgress(0);
    setStatusText("Preparing upload...");

    // Register job in activeJobs array
    const jobData = {
      id: jobId,
      uploadForm,
      files,
      selectedEngagementId,
      resolvedOpportunityName,
      progress: 0,
      statusText: "Preparing upload...",
      error: "",
      busy: true
    };
    
    setActiveJobs(prev => {
      const exists = prev.find(j => j.id === jobId);
      if (exists) return prev.map(j => j.id === jobId ? jobData : j);
      return [...prev, jobData];
    });
    
    if (!isRetry) {
      setViewingJobId(jobId);
    }

    const payload = {
      account_name: currentAccount?.name || "",
      sf_opportunity_id: uploadForm.sfOpportunityId,
      opportunity_name: resolvedOpportunityName,
      contract_type: uploadForm.contractType,
      stage: uploadForm.stage,
      sow_drafter: uploadForm.sowDrafter,
      sow_reviewer: uploadForm.sowReviewer,
      sl_gd_email: uploadForm.slGdEmail,
      created_by: userEmail,
      engagement_id: selectedEngagementId,
      account_id: currentAccount?.account_id,
      files,
    };

    try {
      const result = await analyzeSowBatchUpload(payload, (pct, phase) => {
        const p = clampPct(pct);
        setActiveJobs(prev => prev.map(j => j.id === jobId ? { ...j, progress: p, statusText: phase || j.statusText } : j));
        // Also update local view state if we are currently looking at this job
        setViewingJobId(currentViewingId => {
          if (currentViewingId === jobId) {
            setProgress(p);
            if (phase) setStatusText(phase);
          }
          return currentViewingId;
        });
      });

      // Update completion in local and activeJobs
      setActiveJobs(prev => prev.map(j => j.id === jobId ? { ...j, progress: 100, statusText: "Analysis complete.", busy: false } : j));
      setViewingJobId(currentViewingId => {
        if (currentViewingId === jobId) {
          setProgress(100);
          setStatusText("Analysis complete.");
          setBusy(false);
        }
        return currentViewingId;
      });

      await loadAnalysisHistory(
        activeTab, selectedOpportunityId, opportunities, selectedProjectId,
        currentAccount?.account_id
      );

      const successResults = (Array.isArray(result) ? result : []).filter((r) => r?.id != null);
      if (successResults.length > 0) {
        const last = successResults[successResults.length - 1];
        setViewingJobId(currentViewingId => {
          if (currentViewingId === jobId) setUploadModalOpen(false);
          return currentViewingId;
        });
        
        // Remove from active jobs after success
        setActiveJobs(prev => prev.filter(j => j.id !== jobId));
        
        setMessage(
          successResults.length === 1
            ? "Analysis complete."
            : `Analysis complete for ${successResults.length} files.`
        );
        await selectAnalysis(last.id, last);
      } else if (Array.isArray(result) && result.some((r) => r?.status === "error")) {
        const firstError = result.find((r) => r?.status === "error");
        const errMsg = `Analysis failed: ${firstError?.error || "Unknown error"}`;
        
        setActiveJobs(prev => prev.map(j => j.id === jobId ? { ...j, error: errMsg, busy: false } : j));
        setViewingJobId(currentViewingId => {
          if (currentViewingId === jobId) {
             setError(errMsg);
             setBusy(false);
          }
          return currentViewingId;
        });
      }
    } catch (err) {
      console.error(err);
      
      // Specifically handle validation errors from backend
      const errMsg = getApiError(err) || "Analysis failed. Please check backend logs.";
      
      setActiveJobs(prev => prev.map(j => j.id === jobId ? { ...j, error: errMsg, statusText: "Error while analyzing.", progress: 0, busy: false } : j));
      
      setViewingJobId(currentViewingId => {
        if (currentViewingId === jobId) {
           setError(errMsg);
           setStatusText("Error while analyzing.");
           setProgress(0);
           setBusy(false);
        }
        return currentViewingId;
      });
    }
  };

  const downloadExcel = async (id) => {
    if (!id) return;
    setDownloadingExcel(true);
    try {
      const blob = await downloadExcelReport(id, userEmail);
      downloadBlob(blob, `SOW_Analysis_${id}.xlsx`);
    } catch (err) {
      console.error(err);
      setError(`Failed to download Excel report: ${getApiError(err)}`);
    } finally {
      setDownloadingExcel(false);
    }
  };

  const downloadSowFile = async (id) => {
    if (!id) return;
    setDownloadingSowFile(true);
    try {
      const blob = await fetchSowFileBlob(id, userEmail);
      downloadBlob(blob, selectedDetail?.filename || `SOW_${id}`);
    } catch (err) {
      console.error(err);
      setError(`Failed to download the original SOW: ${getApiError(err)}`);
    } finally {
      setDownloadingSowFile(false);
    }
  };

  const downloadJson = (id) => {
    if (!id || !selectedDetail) return;
    try {
      const raw = safeParseRaw(selectedDetail.raw_json);
      const blob = new Blob([JSON.stringify(raw, null, 2)], { type: "application/json" });
      downloadBlob(blob, `sow_analysis_${id}.json`);
    } catch (err) {
      console.error(err);
      setError("Failed to download JSON.");
    }
  };

  
  const handleImportPlanning = async () => {
    setError("");

    if (!selectedEngagementId) return setError("Please select a Project.");

    const payload = {
      account_name: currentAccount?.name || "",
      sf_opportunity_id: uploadForm.sfOpportunityId,
      opportunity_name: resolvedOpportunityName,
      contract_type: uploadForm.contractType,
      stage: uploadForm.stage,
      sow_drafter: uploadForm.sowDrafter,
      sow_reviewer: uploadForm.sowReviewer,
      sl_gd_email: uploadForm.slGdEmail,
    };

    try {
      const result = await importSowAnalysisPlanningSow(selectedEngagementId, payload, (pct, phase) => {
        setProgress(Math.round(Math.max(0, Math.min(100, pct))));
        if (phase) setStatusText(phase);
      });

      setProgress(100);
      setStatusText("Analysis complete.");
      await loadAnalysisHistory(
        activeTab, selectedOpportunityId, opportunities, selectedProjectId,
        currentAccount?.account_id
      );

      const successResults = (Array.isArray(result) ? result : []).filter((r) => r?.id != null);
      if (successResults.length > 0) {
        const last = successResults[successResults.length - 1];
        setUploadModalOpen(false);
        setMessage("Analysis complete for imported Planning SOW.");
        selectAnalysis(last.id);
      } else {
        const errorResult = (Array.isArray(result) ? result : []).find((r) => r?.status === "error");
        if (errorResult?.error) {
          setError(errorResult.error);
        } else {
          setError("Analysis failed or returned no results.");
        }
      }
    } catch (err) {
      console.error(err);
      setError(getApiError(err, "Analysis failed"));
    } finally {
      setBusy(false);
    }
  };

  return {
    userEmail,

    // workspace context
    currentAccount,
    engagements,
    selectedEngagementId,
    setSelectedEngagementId,
    selectedEngagement,
    opportunityLinked,
    canCreateProject,
    newProjectModalOpen,
    setNewProjectModalOpen,
    registerProject,

    // upload form
    uploadForm,
    onUploadFormChange,
    files,
    setFiles,
    activeJobs,
    uploadModalOpen,
    openUploadModal,
    openUploadModalForJob,
    closeUploadModal,
    dismissJob,
    busy,
    progress,
    statusText,
    handleAnalyze,
    handleImportPlanning,

    // browse: tabs + filter + search
    activeTab,
    setActiveTab,
    opportunities,
    selectedFilterValue,
    setSelectedFilterValue,

    // history list
    analysisHistory,
    filteredHistory,
    search,
    setSearch,
    loading,

    // detail
    selectedAnalysisId,
    selectedDetail,
    detailLoading,
    detailModalOpen,
    selectAnalysis,
    closeDetailModal,

    // feedback
    error,
    setError,
    message,

    // downloads
    downloadExcel,
    downloadingExcel,
    downloadSowFile,
    downloadingSowFile,
    downloadJson,
  };
}
